// ═══════════════════════════════════════════════════════════════════
//  modules/documents/ComptaModule.js
//  ComptaModule
// ═══════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from "react";
import { StatCard } from "../../composants/StatCard";
import { btnBack, cardBox, cardLabel, iconBtn } from "../../composants/styles";
import { API_BASE } from "../../socle/api";
import { fmtEur, fmtGems } from "../../socle/format";
import { useIsMobile } from "../../socle/hooks";
import { THEME } from "../../socle/theme";
import { DOC_CAT_LABELS, DOC_STATUTS, REC_LABELS, REC_MONTHLY } from "./constantes";
import { TransactionForm } from "./TransactionForm";

// ═══════════════ 1) TENUE DE COMPTE ═══════════════
export function ComptaModule({ token, user, onBack }) {
  const isMobile = useIsMobile();
  const [tab,setTab]=useState("coffre");
  const [tx,setTx]=useState([]);
  const [members,setMembers]=useState([]);
  const [loading,setLoading]=useState(true);
  const [showForm,setShowForm]=useState(false);
  const [editing,setEditing]=useState(null);
  const isFounder = user.role==="super_admin";

  const load=useCallback(async()=>{
    setLoading(true);
    try{ const r=await fetch(`${API_BASE}/api/documents/transactions`,{headers:{Authorization:`Bearer ${token}`}}); const d=await r.json(); setTx(Array.isArray(d)?d:[]); }
    catch(e){ console.error(e); setTx([]); }
    setLoading(false);
  }, [token]);
  const loadMembers=useCallback(async()=>{ try{ const r=await fetch(`${API_BASE}/api/documents/members`,{headers:{Authorization:`Bearer ${token}`}}); const d=await r.json(); setMembers(Array.isArray(d)?d:[]); }catch{ setMembers([]); } }, [token]);
  useEffect(()=>{ if(isFounder){ load(); loadMembers(); } },[isFounder, load, loadMembers]);
  const memberName=(id)=>members.find((m)=>m.id===id)?.pseudo || (id?`#${id}`:"—");

  const num=(v)=>Number(v)||0;
  const revenus=tx.filter((t)=>t.type==="revenu").reduce((s,t)=>s+num(t.montant),0);
  const depenses=tx.filter((t)=>t.type==="depense").reduce((s,t)=>s+num(t.montant),0);
  const tresorerie=revenus-depenses;
  const parCategorie={}; tx.filter((t)=>t.type==="depense").forEach((t)=>{const k=t.categorie||"divers"; parCategorie[k]=(parCategorie[k]||0)+num(t.montant);});
  const catEntries=Object.entries(parCategorie).sort((a,b)=>b[1]-a[1]);
  const maxCat=catEntries.length?catEntries[0][1]:0;
  const obligatoire=tx.filter((t)=>t.type==="depense"&&t.nature==="obligatoire").reduce((s,t)=>s+num(t.montant),0);
  const optionnelle=depenses-obligatoire;
  const contribMap={}; tx.filter((t)=>t.type==="depense"&&t.contributeur_id).forEach((t)=>{const id=t.contributeur_id; if(!contribMap[id])contribMap[id]={avance:0,rembourse:0}; contribMap[id].avance+=num(t.montant); if(t.statut==="rembourse")contribMap[id].rembourse+=num(t.montant);});
  const contribEntries=Object.entries(contribMap);
  // ── Abonnements ──────────────────────────────────────────────────────
  //  Un abonnement n'a pas de table à lui : c'est une transaction qui porte
  //  une récurrence. Chaque facture de renouvellement déposée crée donc une
  //  ligne de plus, et le même abonnement apparaît plusieurs fois — trois
  //  lignes Contabo le 12/09, dont une seule valide, et 732,48 € par an de
  //  projection en trop. Deux garde-fous :
  //    · une échéance DÉPASSÉE ne compte plus comme un abonnement actif.
  //      Soit elle a été renouvelée et l'échéance aurait avancé, soit elle
  //      est morte : dans les deux cas la ligne ment, et la machine peut le
  //      voir seule ;
  //    · un bouton retire une ligne du suivi SANS toucher à la dépense
  //      (voir arreterAbonnement).
  const _auj=new Date(); _auj.setHours(0,0,0,0);
  const echeanceDepassee=(t)=>{ if(!t.prochaine_echeance) return false; const d=new Date(String(t.prochaine_echeance).slice(0,10)); return !isNaN(d.getTime()) && d<_auj; };
  const tousAbonnements=tx.filter((t)=>t.recurrence&&t.recurrence!=="ponctuel");
  const abonnements=tousAbonnements.filter((t)=>!echeanceDepassee(t));
  const abonnementsDepasses=tousAbonnements.filter(echeanceDepassee);
  const totalMensuel=abonnements.reduce((s,t)=>s+num(t.montant)*(REC_MONTHLY[t.recurrence]||0),0);
  const totalAnnuel=totalMensuel*12; // projection long terme

  const downloadXlsx=async()=>{
    try{
      const r=await fetch(`${API_BASE}/api/documents/compta/export.xlsx`,{headers:{Authorization:`Bearer ${token}`}});
      if(!r.ok) throw new Error();
      const blob=await r.blob(); const url=URL.createObjectURL(blob); const a=document.createElement("a");
      a.href=url; a.download="compte_de_resultat_orialis.xlsx"; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    }catch{ alert("L'export n'est pas encore disponible (étape 4)."); }
  };
  const removeTx=async(id)=>{ if(!window.confirm("Supprimer cette transaction ?"))return; try{ await fetch(`${API_BASE}/api/documents/transactions/${id}`,{method:"DELETE",headers:{Authorization:`Bearer ${token}`}}); load(); }catch(e){ console.error(e); } };

  //  Retire une ligne du suivi des abonnements SANS toucher à la dépense :
  //  la transaction reste entière dans le compte de résultat, elle passe
  //  simplement en « ponctuel ». C'est ce qu'on veut quand trois factures du
  //  même fournisseur ont créé trois abonnements — les trois dépenses sont
  //  réelles, un seul abonnement l'est. Supprimer la ligne, au contraire,
  //  effacerait une dépense payée et fausserait le compte de résultat.
  //  Réversible : il suffit de remettre une récurrence dans la fiche.
  const arreterAbonnement=async(t)=>{
    if(!window.confirm(`Retirer « ${t.libelle} » du suivi des abonnements ?\n\nLa dépense reste dans le compte de résultat : seule la récurrence est retirée.\nRéversible depuis la fiche de la transaction.`)) return;
    try{
      await fetch(`${API_BASE}/api/documents/transactions/${t.id}`,{
        method:"PUT",
        headers:{Authorization:`Bearer ${token}`,"Content-Type":"application/json"},
        body:JSON.stringify({recurrence:"ponctuel",prochaine_echeance:""}),
      });
      load();
    }catch(e){ console.error(e); }
  };

  if(!isFounder) return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      <button onClick={onBack} style={btnBack}>← Documents</button>
      <div style={{background:THEME.panel,border:`1px solid ${THEME.border}`,borderRadius:14,padding:40,textAlign:"center"}}>
        <div style={{fontSize:38,marginBottom:10}}>🔒</div>
        <div style={{color:THEME.text,fontSize:16,fontWeight:700}}>Accès réservé aux fondateurs</div>
        <div style={{color:THEME.textMuted,fontSize:13,marginTop:6}}>La comptabilité est réservée aux 4 super admin fondateurs.</div>
      </div>
    </div>
  );

  const TABS=[{id:"coffre",label:"💎 Coffre"},{id:"resultat",label:"📊 Compte de résultat"},{id:"contributeurs",label:"👥 Contributeurs"},{id:"abonnements",label:"🔁 Abonnements"},{id:"graphiques",label:"📈 Graphiques"}];

  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      <div style={{display:"flex",alignItems:"center",gap:12}}>
        <button onClick={onBack} style={btnBack}>← Documents</button>
        <h2 style={{color:THEME.text,fontSize:18,fontWeight:800,margin:0}}>Tenue de compte</h2>
        <button onClick={()=>{setEditing(null);setShowForm(true);}} style={{marginLeft:"auto",background:THEME.purpleDim,border:`1px solid ${THEME.purple}`,color:THEME.purple,borderRadius:8,padding:"8px 16px",cursor:"pointer",fontSize:13,fontWeight:700}}>+ Nouvelle transaction</button>
      </div>
      <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
        {TABS.map((t)=>(
          <button key={t.id} onClick={()=>setTab(t.id)} style={{background:tab===t.id?THEME.accentDim:"transparent",border:`1px solid ${tab===t.id?THEME.accent:THEME.border}`,color:tab===t.id?THEME.accent:THEME.textMuted,borderRadius:8,padding:"7px 14px",cursor:"pointer",fontSize:13,fontWeight:tab===t.id?700:400}}>{t.label}</button>
        ))}
      </div>

      {loading ? <div style={{color:THEME.textMuted,padding:20}}>Chargement…</div> : (
        <>
          {tab==="coffre" && (
            <div style={{display:"flex",flexDirection:"column",gap:16}}>
              <div style={{position:"relative",overflow:"hidden",borderRadius:18,background:`radial-gradient(120% 140% at 50% 0%, ${THEME.purpleDim} 0%, ${THEME.panel} 55%, ${THEME.surface} 100%)`,border:`1px solid ${THEME.purple}40`,padding:"26px 24px",display:"flex",alignItems:"center",gap:28,flexWrap:"wrap",justifyContent:"center"}}>
                <img src="/coffre_projet.png" alt="Coffre du projet" style={{width:240,maxWidth:"45vw",filter:"drop-shadow(0 8px 24px rgba(124,58,237,0.45))"}} />
                <div style={{textAlign:"center"}}>
                  <div style={{color:THEME.textMuted,fontSize:12,letterSpacing:2,textTransform:"uppercase"}}>Trésor du projet</div>
                  <div style={{color:tresorerie>=0?THEME.gold:THEME.red,fontSize:46,fontWeight:900,lineHeight:1.1,marginTop:4}}>{fmtGems(tresorerie)} <span style={{fontSize:30}}>💎</span></div>
                  <div style={{color:THEME.textMuted,fontSize:13,marginTop:2}}>soit {fmtEur(tresorerie)}</div>
                </div>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(180px, 1fr))",gap:12}}>
                <StatCard label="Revenus" value={fmtEur(revenus)} color={THEME.emerald} icon="📥" />
                <StatCard label="Dépenses" value={fmtEur(depenses)} color={THEME.red} icon="📤" />
                <StatCard label="Transactions" value={tx.length} color={THEME.accent} icon="🧾" />
              </div>
              <div style={{background:THEME.panel,border:`1px solid ${THEME.border}`,borderRadius:14,padding:16}}>
                <div style={{color:THEME.text,fontSize:14,fontWeight:700,marginBottom:12}}>Contributions par membre</div>
                {contribEntries.length===0 ? <div style={{color:THEME.textMuted,fontSize:13}}>Aucune avance enregistrée pour l'instant.</div> : contribEntries.map(([id,c])=>{
                  const reste=c.avance-c.rembourse;
                  return (
                    <div key={id} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 0",borderBottom:`1px solid ${THEME.border}`}}>
                      <div style={{width:30,height:30,borderRadius:8,background:THEME.purpleDim,color:THEME.purple,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700,fontSize:13}}>{memberName(Number(id))[0]?.toUpperCase()||"?"}</div>
                      <div style={{color:THEME.text,fontSize:13,fontWeight:600}}>{memberName(Number(id))}</div>
                      <div style={{marginLeft:"auto",color:reste>0?THEME.amber:THEME.emerald,fontSize:13,fontWeight:700}}>{reste>0?`${fmtEur(reste)} à rembourser`:"à jour ✓"}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {tab==="resultat" && (
            <div style={{display:"flex",flexDirection:"column",gap:16}}>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(160px, 1fr))",gap:12}}>
                <StatCard label="Produits" value={fmtEur(revenus)} color={THEME.emerald} icon="➕" />
                <StatCard label="Charges" value={fmtEur(depenses)} color={THEME.red} icon="➖" />
                <StatCard label="Résultat" value={fmtEur(tresorerie)} color={tresorerie>=0?THEME.gold:THEME.red} icon="🟰" />
                <StatCard label="Charges récurrentes / an" value={fmtEur(totalAnnuel)} color={THEME.amber} icon="🔁" />
              </div>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                <div style={{color:THEME.text,fontSize:14,fontWeight:700}}>Détail des transactions</div>
                <button onClick={downloadXlsx} style={{background:THEME.emeraldDim,border:`1px solid ${THEME.emerald}`,color:THEME.emerald,borderRadius:8,padding:"7px 14px",cursor:"pointer",fontSize:13,fontWeight:700}}>⬇ Exporter .xlsx</button>
              </div>
              {tx.length===0 ? (
                <div style={{background:THEME.panel,border:`1px solid ${THEME.border}`,borderRadius:14,padding:20,color:THEME.textMuted,fontSize:13}}>Aucune transaction. Clique sur « + Nouvelle transaction » pour commencer.</div>
              ) : isMobile ? (
                <div>
                  {tx.map((t)=>(
                    <div key={t.id} style={cardBox}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:10,marginBottom:6}}>
                        <span style={{color:THEME.text,fontWeight:600,fontSize:14}}>{t.libelle}</span>
                        <span style={{fontWeight:700,fontSize:14,whiteSpace:"nowrap",color:t.type==="revenu"?THEME.emerald:THEME.red}}>{t.type==="revenu"?"+":"−"}{fmtEur(t.montant)}</span>
                      </div>
                      <div style={{display:"flex",flexWrap:"wrap",gap:"4px 12px",color:THEME.textMuted,fontSize:12}}>
                        <span>{(t.date_operation||"").slice(0,10)}</span>
                        <span>{DOC_CAT_LABELS[t.categorie]||t.categorie||"—"}</span>
                        {t.statut && <span style={{color:THEME.amber}}>{DOC_STATUTS[t.statut]}</span>}
                      </div>
                      <div style={{display:"flex",gap:6,justifyContent:"flex-end",marginTop:8,borderTop:`1px solid ${THEME.border}`,paddingTop:8}}>
                        <button onClick={()=>{setEditing(t);setShowForm(true);}} title="Éditer" style={iconBtn}>✏️</button>
                        <button onClick={()=>removeTx(t.id)} title="Supprimer" style={iconBtn}>🗑️</button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
              <div style={{background:THEME.panel,border:`1px solid ${THEME.border}`,borderRadius:14,overflow:"hidden"}}>
                <div style={{display:"grid",gridTemplateColumns:"90px 1fr 110px 120px 110px 80px",padding:"10px 14px",borderBottom:`1px solid ${THEME.border}`,color:THEME.textMuted,fontSize:11,fontWeight:700,textTransform:"uppercase"}}>
                  <div>Date</div><div>Libellé</div><div>Catégorie</div><div>Statut</div><div style={{textAlign:"right"}}>Montant</div><div></div>
                </div>
                {tx.map((t)=>(
                  <div key={t.id} style={{display:"grid",gridTemplateColumns:"90px 1fr 110px 120px 110px 80px",padding:"10px 14px",borderBottom:`1px solid ${THEME.border}`,alignItems:"center",fontSize:13}}>
                    <div style={{color:THEME.textMuted,fontSize:12}}>{(t.date_operation||"").slice(0,10)}</div>
                    <div style={{color:THEME.text}}>{t.libelle}</div>
                    <div style={{color:THEME.textMuted,fontSize:12}}>{DOC_CAT_LABELS[t.categorie]||t.categorie||"—"}</div>
                    <div style={{fontSize:11}}>{t.statut?<span style={{color:THEME.amber}}>{DOC_STATUTS[t.statut]}</span>:<span style={{color:THEME.textDim}}>—</span>}</div>
                    <div style={{textAlign:"right",fontWeight:700,color:t.type==="revenu"?THEME.emerald:THEME.red}}>{t.type==="revenu"?"+":"−"}{fmtEur(t.montant)}</div>
                    <div style={{textAlign:"right",display:"flex",gap:6,justifyContent:"flex-end"}}>
                      <button onClick={()=>{setEditing(t);setShowForm(true);}} title="Éditer" style={iconBtn}>✏️</button>
                      <button onClick={()=>removeTx(t.id)} title="Supprimer" style={iconBtn}>🗑️</button>
                    </div>
                  </div>
                ))}
              </div>
              )}
            </div>
          )}

          {tab==="contributeurs" && (
            <div style={{background:THEME.panel,border:`1px solid ${THEME.border}`,borderRadius:14,padding:16}}>
              <div style={{color:THEME.text,fontSize:14,fontWeight:700,marginBottom:12}}>Équité — qui a avancé quoi</div>
              {!isMobile && <div style={{display:"grid",gridTemplateColumns:"1fr 130px 130px 130px",padding:"8px 0",borderBottom:`1px solid ${THEME.border}`,color:THEME.textMuted,fontSize:11,fontWeight:700,textTransform:"uppercase"}}>
                <div>Membre</div><div style={{textAlign:"right"}}>Avancé</div><div style={{textAlign:"right"}}>Remboursé</div><div style={{textAlign:"right"}}>Reste dû</div>
              </div>}
              {contribEntries.length===0 && <div style={{padding:16,color:THEME.textMuted,fontSize:13}}>Aucune avance enregistrée.</div>}
              {contribEntries.map(([id,c])=>{ const reste=c.avance-c.rembourse; return (
                isMobile ? (
                <div key={id} style={cardBox}>
                  <div style={{color:THEME.text,fontWeight:600,fontSize:14,marginBottom:6}}>{memberName(Number(id))}</div>
                  <div style={{display:"flex",justifyContent:"space-between",padding:"2px 0",fontSize:12}}><span style={cardLabel}>Avancé</span><span style={{color:THEME.text}}>{fmtEur(c.avance)}</span></div>
                  <div style={{display:"flex",justifyContent:"space-between",padding:"2px 0",fontSize:12}}><span style={cardLabel}>Remboursé</span><span style={{color:THEME.emerald}}>{fmtEur(c.rembourse)}</span></div>
                  <div style={{display:"flex",justifyContent:"space-between",padding:"2px 0",fontSize:12}}><span style={cardLabel}>Reste dû</span><span style={{color:reste>0?THEME.amber:THEME.emerald,fontWeight:700}}>{fmtEur(reste)}</span></div>
                </div>
                ) : (
                <div key={id} style={{display:"grid",gridTemplateColumns:"1fr 130px 130px 130px",padding:"10px 0",borderBottom:`1px solid ${THEME.border}`,fontSize:13,alignItems:"center"}}>
                  <div style={{color:THEME.text,fontWeight:600}}>{memberName(Number(id))}</div>
                  <div style={{textAlign:"right",color:THEME.text}}>{fmtEur(c.avance)}</div>
                  <div style={{textAlign:"right",color:THEME.emerald}}>{fmtEur(c.rembourse)}</div>
                  <div style={{textAlign:"right",color:reste>0?THEME.amber:THEME.emerald,fontWeight:700}}>{fmtEur(reste)}</div>
                </div>
                )
              );})}
            </div>
          )}

          {tab==="abonnements" && (()=>{
            //  Deux listes : les actifs, et ceux dont l'échéance est passée.
            //  Les compteurs ne portent QUE sur les actifs — c'est là qu'était
            //  l'erreur de budget.
            const Actions=({t})=>(
              <div style={{display:"flex",gap:4,justifyContent:"flex-end"}}>
                <button onClick={()=>arreterAbonnement(t)} title="Retirer du suivi des abonnements (la dépense reste au compte de résultat)" style={iconBtn}>⏹️</button>
                <button onClick={()=>removeTx(t.id)} title="Supprimer la transaction — efface aussi la dépense" style={iconBtn}>🗑️</button>
              </div>
            );
            const Carte=({t,perime})=>(
              <div key={t.id} style={{...cardBox,opacity:perime?0.55:1}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:10,marginBottom:6}}>
                  <span style={{color:THEME.text,fontWeight:600,fontSize:14}}>{t.libelle}</span>
                  <span style={{color:THEME.red,fontWeight:700,fontSize:14,whiteSpace:"nowrap"}}>{fmtEur(t.montant)}</span>
                </div>
                <div style={{display:"flex",flexWrap:"wrap",gap:"4px 12px",color:THEME.textMuted,fontSize:12,marginBottom:8}}>
                  <span>{DOC_CAT_LABELS[t.categorie]||t.categorie||"—"}</span>
                  <span>{REC_LABELS[t.recurrence]||t.recurrence}</span>
                  {t.prochaine_echeance && <span style={{color:perime?THEME.red:THEME.amber,fontWeight:perime?700:400}}>Renouv. {String(t.prochaine_echeance).slice(0,10)}{perime?" — dépassée":""}</span>}
                </div>
                <Actions t={t} />
              </div>
            );
            const Ligne=({t,perime})=>(
              <div key={t.id} style={{display:"grid",gridTemplateColumns:"1fr 120px 100px 110px 120px 78px",padding:"10px 14px",borderBottom:`1px solid ${THEME.border}`,fontSize:13,alignItems:"center",opacity:perime?0.55:1}}>
                <div style={{color:THEME.text}}>{t.libelle}</div>
                <div style={{color:THEME.textMuted,fontSize:12}}>{DOC_CAT_LABELS[t.categorie]||t.categorie||"—"}</div>
                <div style={{color:THEME.textMuted,fontSize:12}}>{REC_LABELS[t.recurrence]||t.recurrence}</div>
                <div style={{textAlign:"right",color:THEME.red,fontWeight:700}}>{fmtEur(t.montant)}</div>
                <div style={{textAlign:"right",color:perime?THEME.red:THEME.amber,fontSize:12,fontWeight:600}}>{t.prochaine_echeance?String(t.prochaine_echeance).slice(0,10):"—"}</div>
                <Actions t={t} />
              </div>
            );
            const Entete=()=>(
              <div style={{display:"grid",gridTemplateColumns:"1fr 120px 100px 110px 120px 78px",padding:"10px 14px",borderBottom:`1px solid ${THEME.border}`,color:THEME.textMuted,fontSize:11,fontWeight:700,textTransform:"uppercase"}}>
                <div>Libellé</div><div>Catégorie</div><div>Récurrence</div><div style={{textAlign:"right"}}>Montant</div><div style={{textAlign:"right"}}>Renouvellement</div><div />
              </div>
            );
            return (
            <div style={{display:"flex",flexDirection:"column",gap:12}}>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(180px, 1fr))",gap:12}}>
                <StatCard label="Coût mensuel récurrent" value={fmtEur(totalMensuel)} color={THEME.amber} icon="🔁" />
                <StatCard label="Coût annuel projeté" value={fmtEur(totalAnnuel)} color={THEME.red} icon="📅" />
                <StatCard label="Abonnements actifs" value={abonnements.length} color={THEME.accent} icon="🧾" />
              </div>
              <div style={{color:THEME.textDim,fontSize:12}}>Projection sur la base des abonnements actuels — pour anticiper le budget sur le long terme. Les échéances dépassées sont exclues du calcul.</div>

              <div style={{background:THEME.panel,border:`1px solid ${THEME.border}`,borderLeft:`3px solid ${THEME.accent}`,borderRadius:10,padding:"10px 14px",color:THEME.textMuted,fontSize:12,lineHeight:1.55}}>
                <b style={{color:THEME.text}}>Une facture déposée crée une ligne, elle n'en met aucune à jour.</b> Trois factures du même fournisseur donnent donc trois abonnements, alors qu'un seul est en cours.<br />
                <span style={{color:THEME.accent}}>⏹️</span> retire la ligne du suivi et <b style={{color:THEME.text}}>garde la dépense</b> au compte de résultat — c'est le bon geste ici.<br />
                <span style={{color:THEME.red}}>🗑️</span> supprime la transaction et <b style={{color:THEME.text}}>efface la dépense</b> — à réserver aux documents analysés deux fois.
              </div>

              {abonnements.length===0 && abonnementsDepasses.length===0 ? (
                <div style={{background:THEME.panel,border:`1px solid ${THEME.border}`,borderRadius:14,padding:20,color:THEME.textMuted,fontSize:13}}>Aucun abonnement récurrent. Les factures récurrentes (mensuel/annuel) apparaîtront ici.</div>
              ) : isMobile ? (
                <div>{abonnements.map((t)=><Carte key={t.id} t={t} perime={false} />)}</div>
              ) : abonnements.length>0 ? (
                <div style={{background:THEME.panel,border:`1px solid ${THEME.border}`,borderRadius:14,overflow:"hidden"}}>
                  <Entete />
                  {abonnements.map((t)=><Ligne key={t.id} t={t} perime={false} />)}
                </div>
              ) : (
                <div style={{background:THEME.panel,border:`1px solid ${THEME.border}`,borderRadius:14,padding:20,color:THEME.textMuted,fontSize:13}}>Aucun abonnement actif : toutes les échéances sont dépassées.</div>
              )}

              {abonnementsDepasses.length>0 && (
                <div style={{display:"flex",flexDirection:"column",gap:8,marginTop:6}}>
                  <div style={{...cardLabel,color:THEME.red,fontWeight:700,fontSize:12}}>⚠️ Échéance dépassée — {abonnementsDepasses.length} ligne{abonnementsDepasses.length>1?"s":""}, hors du calcul</div>
                  <div style={{color:THEME.textMuted,fontSize:12}}>Un abonnement dont la prochaine échéance est passée n'est plus actif : soit il a été renouvelé et l'échéance aurait dû avancer, soit il est terminé. Dans les deux cas la ligne ne dit plus la vérité.</div>
                  {isMobile ? (
                    <div>{abonnementsDepasses.map((t)=><Carte key={t.id} t={t} perime />)}</div>
                  ) : (
                    <div style={{background:THEME.panel,border:`1px solid ${THEME.border}`,borderRadius:14,overflow:"hidden"}}>
                      <Entete />
                      {abonnementsDepasses.map((t)=><Ligne key={t.id} t={t} perime />)}
                    </div>
                  )}
                </div>
              )}
            </div>
            );
          })()}

          {tab==="graphiques" && (
            <div style={{display:"flex",flexDirection:"column",gap:16}}>
              <div style={{background:THEME.panel,border:`1px solid ${THEME.border}`,borderRadius:14,padding:18}}>
                <div style={{color:THEME.text,fontSize:14,fontWeight:700,marginBottom:14}}>Dépenses par catégorie</div>
                {catEntries.length===0 ? <div style={{color:THEME.textMuted,fontSize:13}}>Pas encore de dépenses.</div> : catEntries.map(([cat,val])=>(
                  <div key={cat} style={{marginBottom:10}}>
                    <div style={{display:"flex",justifyContent:"space-between",fontSize:12,marginBottom:4}}>
                      <span style={{color:THEME.textMuted}}>{DOC_CAT_LABELS[cat]||cat}</span>
                      <span style={{color:THEME.text,fontWeight:600}}>{fmtEur(val)}</span>
                    </div>
                    <div style={{height:10,background:THEME.surface,borderRadius:6,overflow:"hidden"}}>
                      <div style={{height:"100%",width:`${maxCat?(val/maxCat)*100:0}%`,background:`linear-gradient(90deg, ${THEME.purple}, ${THEME.accent})`,borderRadius:6}} />
                    </div>
                  </div>
                ))}
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                <StatCard label="Charges obligatoires" value={fmtEur(obligatoire)} color={THEME.red} icon="📌" />
                <StatCard label="Charges optionnelles" value={fmtEur(optionnelle)} color={THEME.amber} icon="✨" />
              </div>
              <div style={{color:THEME.textDim,fontSize:12}}>📈 Le graphe « dépenses vs revenus dans le temps » sera ajouté plus tard (lib de graphes).</div>
            </div>
          )}
        </>
      )}

      {showForm && <TransactionForm token={token} members={members} editing={editing} onClose={()=>{setShowForm(false);setEditing(null);}} onSaved={()=>{setShowForm(false);setEditing(null);load();}} />}
    </div>
  );
}
